import { useState } from 'react';
import {
  apiErrorText,
  useDecideWriteOffNameMatchMutation,
  useListWriteOffNameMatchesQuery,
  type WriteOffNameMatch,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { DecisionBtns, Err, Muted, Table, TableTitle } from '../styles';

function sideText(name: string, unit: string | null): string {
  return unit ? `${name} (${unit})` : name;
}

function confidenceText(m: WriteOffNameMatch): string {
  if (m.source === 'llm' && m.confidence !== null) {
    return `ИИ: ${Math.round(m.confidence * 100)}%`;
  }
  return `сходство: ${Math.round(m.similarity * 100)}%`;
}

/**
 * Предложенные пары «норматив ↔ накладная» для материалов, которые не нашли
 * себя по точному названию. Пара входит в баланс М-29 только после кнопки
 * «Это один материал»; до этого итог не меняется.
 */
export function NameMatchesBlock({
  checkId,
  can_write,
}: {
  checkId: string;
  can_write: boolean;
}) {
  const q = useListWriteOffNameMatchesQuery(checkId);
  const [decide, { isLoading }] = useDecideWriteOffNameMatchMutation();
  const [error, setError] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  const status = q.data?.status ?? null;
  const llmFailed = status?.status === 'llm_unavailable';
  if (q.isError) {
    return (
      <>
        <TableTitle>Предложенные пары</TableTitle>
        <Err $mt={8}>{apiErrorText(q.error, 'Не удалось загрузить предложенные пары.')}</Err>
      </>
    );
  }
  if (items.length === 0 && !llmFailed) return null;

  async function onDecide(m: WriteOffNameMatch, decision: 'confirmed' | 'rejected') {
    setError(null);
    try {
      await decide({ id: m.id, checkId, decision }).unwrap();
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось сохранить решение по паре.'));
    }
  }

  return (
    <>
      <TableTitle>
        Предложенные пары <Badge $tone="neutral">{items.length}</Badge>
      </TableTitle>
      <Muted $mt={4}>
        Материал из накладной под другим названием. Пара войдёт в сравнение только после
        кнопки «Это один материал».
      </Muted>
      {llmFailed && (
        <Muted $mt={4}>
          {status?.message ??
            'Сопоставление названий не выполнено (ИИ недоступен) — пары предложены только по сходству названий.'}
        </Muted>
      )}
      {error && <Err $mt={8}>{error}</Err>}
      {items.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Норматив</th>
              <th>Накладная</th>
              <th>Оценка</th>
              <th>Решение</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id}>
                <td>{sideText(m.norm_name, m.norm_unit)}</td>
                <td>
                  {sideText(m.fact_name, m.fact_unit)}
                  {m.reason && <Muted>общее: {m.reason}</Muted>}
                </td>
                <td>{confidenceText(m)}</td>
                <td>
                  {m.status === 'confirmed' && <Badge $tone="success">один материал</Badge>}
                  {m.status === 'rejected' && <Badge $tone="neutral">разные материалы</Badge>}
                  <DecisionBtns>
                    {m.status !== 'confirmed' && (
                      <Button
                        $variant="ghost"
                        $size="sm"
                        type="button"
                        disabled={isLoading || !can_write}
                        title={!can_write ? 'Только чтение' : undefined}
                        onClick={() => onDecide(m, 'confirmed')}
                      >
                        Это один материал
                      </Button>
                    )}
                    {m.status !== 'rejected' && (
                      <Button
                        $variant="ghost"
                        $size="sm"
                        type="button"
                        disabled={isLoading || !can_write}
                        title={!can_write ? 'Только чтение' : undefined}
                        onClick={() => onDecide(m, 'rejected')}
                      >
                        Разные материалы
                      </Button>
                    )}
                  </DecisionBtns>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}
